import { useEffect, useState } from 'react';
import { api } from '../api.js';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const STATUS = {
  given: { mark: null, label: 'Given', cls: 'g' },          // shows the initial
  refused: { mark: 'R', label: 'Refused', cls: 'r' },
  omitted: { mark: 'O', label: 'Omitted', cls: 'o' },
  unavailable: { mark: 'X', label: 'Not available', cls: 'o' },
  self: { mark: 'S', label: 'Self-administered', cls: 'g' },
};
const SLOTS = ['MORNING', 'NOON', 'EVENING', 'NIGHT'];

export default function Meds({ caseObj, me, onBack }) {
  const caseId = typeof caseObj === 'string' ? caseObj : caseObj.id;
  const [meds, setMeds] = useState([]);
  const [admin, setAdmin] = useState([]);
  const [pt, setPt] = useState({});
  const [myInitials, setMyInitials] = useState('');
  const [week, setWeek] = useState(mondayOf(todayMY()));
  const [tab, setTab] = useState('regular');
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(blank('regular'));
  const [status, setStatus] = useState('');
  const [aiMedOpen, setAiMedOpen] = useState(false);
  const [aiMedCopied, setAiMedCopied] = useState(false);

  const days = [...Array(7)].map((_, i) => addDays(week, i));
  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(''), 3000); };

  const load = () => api.getMeds(caseId, days[0], days[6]).then((d) => {
    setMeds(d.meds || []); setAdmin(d.admin || []); setPt(d.patient || {});
  }).catch((e) => flash(e.message));

  useEffect(() => { load(); }, [caseId, week]);
  useEffect(() => {
    api.getTeam().then((d) => {
      const mine = (d.team || []).find((t) => t.id === d.me);
      setMyInitials(mine ? mine.initials : '');
    }).catch(() => {});
  }, []);

  const at = (medId, date, slot) =>
    admin.find((a) => a.med_id === medId && a.given_date === date && a.slot === slot);

  async function sign(med, date, slot) {
    const ex = at(med.id, date, slot);
    let st = 'given', reason = '';
    if (ex) {                       // tapping again cycles through the outcomes
      const order = ['given', 'refused', 'omitted', 'unavailable', 'self'];
      st = order[(order.indexOf(ex.status) + 1) % order.length];
    }
    if (st !== 'given' && st !== 'self') {
      reason = prompt(STATUS[st].label + ' — why?') || '';
      if (!reason) return;
    }
    try {
      await api.giveMed({ case_id: caseId, med_id: med.id, given_date: date,
        slot, status: st, reason });
      load();
    } catch (e) { flash(e.message); }
  }

  async function addMed() {
    if (!f.name.trim()) { flash('Medication name is needed'); return; }
    try {
      await api.addMed(caseId, { ...f, kind: tab });
      setF(blank(tab)); setAdding(false); flash('✓ Added'); load();
    } catch (e) { flash(e.message); }
  }

  async function stopMed(m) {
    if (!confirm((m.active ? 'Stop' : 'Resume') + ' ' + m.name + '?')) return;
    try { await api.updateMed(m.id, { action: m.active ? 'stop' : 'resume' }); load(); }
    catch (e) { flash(e.message); }
  }

  const list = meds.filter((m) => (m.kind || 'regular') === tab);

  return (
    <div className="card">
      <button className="link" onClick={onBack}>← Back</button>
      <h2>Medication chart {pt.name ? '— ' + pt.name : ''}</h2>
      <div className="medhead">
        <span><b>Name:</b> {pt.name || '—'}</span>
        <span className={pt.allergies ? 'allergy' : ''}>
          <b>Allergies:</b> {pt.allergies || 'None known'}</span>
        <button
          type="button"
          className="pri"
          style={{
            marginLeft: 'auto',
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            border: '1px solid #38bdf8',
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.8rem',
            padding: '5px 12px',
            borderRadius: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(2, 132, 199, 0.4)'
          }}
          onClick={() => setAiMedOpen(true)}
        >
          <span>🧠 ⚡</span> AI Drug Safety &amp; Interaction Sentinel ({meds.length})
        </button>
      </div>
      {status && <p className="status">{status}</p>}

      <div className="signbar">
        <span className="sigchip">{myInitials}</span>
        <span className="muted">Doses you sign are recorded under your initials.</span>
      </div>

      <div className="tabs">
        <button className={tab === 'regular' ? 'on' : ''} onClick={() => { setTab('regular'); setF(blank('regular')); }}>
          Regular</button>
        <button className={tab === 'stat' ? 'on' : ''} onClick={() => { setTab('stat'); setF(blank('stat')); }}>
          STAT</button>
        <button className={tab === 'prn' ? 'on' : ''} onClick={() => { setTab('prn'); setF(blank('prn')); }}>
          PRN (when necessary)</button>
      </div>

      {tab === 'regular' && (
        <div className="rosbar">
          <button className="ghost" onClick={() => setWeek(addDays(week, -7))}>◀ Prev</button>
          <b>{fmt(days[0])} – {fmt(days[6])}</b>
          <button className="ghost" onClick={() => setWeek(addDays(week, 7))}>Next ▶</button>
          <button className="ghost" onClick={() => setWeek(mondayOf(todayMY()))}>This week</button>
        </div>
      )}

      <button className="ghost wide" onClick={() => setAdding(!adding)}>
        {adding ? '▾ Cancel' : '＋ Add a medication'}</button>

      {adding && (
        <div className="hoform">
          <div className="f"><label>Medication</label>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              placeholder="e.g. Metformin 二甲双胍" /></div>
          <div className="grid3">
            <div className="f"><label>Dose</label>
              <input value={f.dose} onChange={(e) => setF({ ...f, dose: e.target.value })} placeholder="500mg" /></div>
            <div className="f"><label>Route</label>
              <input list="routes" value={f.route} onChange={(e) => setF({ ...f, route: e.target.value })} placeholder="PO" />
              <datalist id="routes">{['PO','SC','IM','IV','TOP','PR','SL','NEB','INH','NG','PEG','EYE','EAR']
                .map((r) => <option key={r} value={r} />)}</datalist></div>
            <div className="f"><label>Frequency</label>
              <input list="freqs" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} placeholder="BD" />
              <datalist id="freqs">{['OD','BD','TDS','QID','ON','OM','STAT','PRN','Weekly']
                .map((r) => <option key={r} value={r} />)}</datalist></div>
          </div>
          {tab === 'regular' && (
            <div className="f"><label>Times of day</label>
              <div className="seg">
                {SLOTS.map((s) => {
                  const on = (f.times || '').split(',').filter(Boolean).includes(s);
                  return <button key={s} className={on ? 'on' : ''} onClick={() => {
                    const cur = (f.times || '').split(',').filter(Boolean);
                    setF({ ...f, times: (on ? cur.filter((x) => x !== s) : [...cur, s]).join(',') });
                  }}>{s}</button>;
                })}
              </div></div>
          )}
          {tab === 'prn' && <div className="grid2">
            <div className="f"><label>Indication</label>
              <input value={f.indication} onChange={(e) => setF({ ...f, indication: e.target.value })}
                placeholder="e.g. for pain" /></div>
            <div className="f"><label>Maximum dose</label>
              <input value={f.max_dose} onChange={(e) => setF({ ...f, max_dose: e.target.value })}
                placeholder="e.g. 4g in 24 hours" /></div>
          </div>}
          <div className="f"><label>Instruction / notes</label>
            <textarea rows="2" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              placeholder="e.g. after food" /></div>
          <button className="pri wide" onClick={addMed}>Add medication</button>
        </div>
      )}

      {list.length === 0 && <p className="muted">No {tab} medication charted.</p>}

      {/* ---- regular: weekly grid ---- */}
      {tab === 'regular' && list.length > 0 && (
        <div className="rosgrid marwrap">
          <table className="mar">
            <thead><tr>
              <th className="mname">Medication</th><th className="mslot">Time</th>
              {days.map((d, i) => <th key={d}><span>{DAYS[i]}</span>{fmt(d)}</th>)}
            </tr></thead>
            <tbody>
              {list.map((m) => {
                const slots = (m.times || '').split(',').filter(Boolean);
                const rows = slots.length ? slots : ['DOSE'];
                return rows.map((slot, si) => (
                  <tr key={m.id + slot} className={m.active ? '' : 'stopped'}>
                    {si === 0 && (
                      <td className="mname" rowSpan={rows.length}>
                        <b>{m.name}</b>
                        <span className="mmeta">{[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}</span>
                        {m.notes ? <span className="mnote">{m.notes}</span> : null}
                        <button className="link xs" onClick={() => stopMed(m)}>
                          {m.active ? 'stop' : 'resume'}</button>
                      </td>
                    )}
                    <td className="mslot">{slot}</td>
                    {days.map((d) => {
                      const a = at(m.id, d, slot);
                      const S = a ? STATUS[a.status] || STATUS.given : null;
                      return (
                        <td key={d} className={'mcell ' + (S ? S.cls : '')}
                          onClick={() => m.active && sign(m, d, slot)}
                          title={a ? (S.label + (a.reason ? ' — ' + a.reason : '') + ' · ' + a.staff_initial) : 'Tap to sign'}>
                          {a ? (S.mark || a.staff_initial) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- stat / prn: one row per dose given ---- */}
      {tab !== 'regular' && list.map((m) => {
        const given = admin.filter((a) => a.med_id === m.id)
          .sort((x, y) => y.given_at - x.given_at);
        return (
          <div className={'rec med ' + (m.active ? '' : 'stopped')} key={m.id}>
            <div className="rec-head">
              <div className="grow">
                <b>{m.name}</b>
                <div className="meta">{[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}</div>
                {m.indication ? <div className="meta">For: {m.indication}</div> : null}
                {m.max_dose ? <div className="meta warn">Max: {m.max_dose}</div> : null}
                {m.notes ? <div className="mnote">{m.notes}</div> : null}
              </div>
              <div className="pbtns">
                {m.active && <button className="pri sm"
                  onClick={() => sign(m, todayMY(), 'dose-' + Date.now())}>Give now</button>}
                <button className="ghost sm" onClick={() => stopMed(m)}>
                  {m.active ? 'Stop' : 'Resume'}</button>
              </div>
            </div>
            {given.length > 0 && (
              <div className="gavelist">
                {given.slice(0, 8).map((a) => (
                  <div className="gave" key={a.id}>
                    <span className="gt">{a.given_date} {timeOf(a.given_at)}</span>
                    <span className={'gs ' + (STATUS[a.status] || STATUS.given).cls}>
                      {(STATUS[a.status] || STATUS.given).label}</span>
                    <span className="gi">{a.staff_initial}</span>
                    {a.reason ? <span className="gr">{a.reason}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <p className="hint">Tap a box to sign. Tap again to change it: initials = given,
        R = refused, O = omitted, X = not available, S = self-administered.</p>

      {/* AI Drug Safety & Interaction Sentinel Modal */}
      {aiMedOpen && (() => {
        const HIGH_ALERT_KEYWORDS = ['insulin', 'warfarin', 'heparin', 'aspirin', 'clopidogrel', 'plavix', 'metformin', 'digoxin', 'tramadol', 'morphine', 'fentanyl', 'midazolam', 'lorazepam', 'potassium', 'glimepiride', 'gliclazide', 'rivaroxaban', 'xarelto', 'apixaban', 'eliquis'];
        const highAlertList = meds.filter(m => HIGH_ALERT_KEYWORDS.some(k => (m.name || '').toLowerCase().includes(k)));
        const allergyMatches = pt.allergies && pt.allergies !== 'None known' 
          ? meds.filter(m => pt.allergies.toLowerCase().split(/[,\s]+/).some(a => a.length > 2 && (m.name || '').toLowerCase().includes(a)))
          : [];
        const isPolypharmacy = meds.length >= 5;

        const medReportText = `MEDICATION SAFETY & INTERACTION AUDIT · ASSURA NURSING
--------------------------------------------------------
PATIENT: ${pt.name || 'Patient'}
ALLERGIES: ${pt.allergies || 'None known'}
TOTAL PRESCRIBED MEDS: ${meds.length} ${isPolypharmacy ? '⚠️ [POLYPHARMACY >= 5 MEDS]' : '✓ Normal count'}

HIGH-ALERT DRUGS IDENTIFIED (${highAlertList.length}):
${highAlertList.length ? highAlertList.map(m => `• ${m.name} (${m.dose || '—'}, ${m.frequency || '—'})`).join('\n') : '✓ None'}

ALLERGY WARNINGS:
${allergyMatches.length ? allergyMatches.map(m => `⚠️ CRITICAL: Prescribed ${m.name} may cross-react with stated allergy: ${pt.allergies}`).join('\n') : '✓ No allergy conflicts detected.'}

CLINICAL SAFETY DIRECTIVES (MOH / BEERS CRITERIA):
1. Insulin / Hypoglycemic Agents: Verify capillary blood glucose (HGT) prior to administration. Target 4.0 - 8.0 mmol/L.
2. Anticoagulants / Antiplatelets: Monitor for bruising, epistaxis, hematuria, or dark stools.
3. Renal Clearance: Ensure hydration status and review serum creatinine periodically.`;

        return (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(3, 10, 24, 0.88)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16
          }}>
            <div style={{
              background: 'linear-gradient(145deg, #071e3d 0%, #041226 100%)',
              border: '1.5px solid rgba(56, 189, 248, 0.4)',
              borderRadius: 16,
              width: '100%',
              maxWidth: 680,
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: 24,
              color: '#f8fafc',
              boxShadow: '0 25px 60px rgba(0,0,0,0.8)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(56, 189, 248, 0.25)', paddingBottom: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.6rem' }}>💊</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#fff' }}>AI Drug Safety &amp; Interaction Sentinel</h3>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>High-Alert Screening · BEERS Criteria · Cross-Allergy Surveillance</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAiMedOpen(false)}
                  style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  ✕
                </button>
              </div>

              {/* Status Chips */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
                <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Total Active Drugs</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc', margin: '4px 0 2px' }}>{meds.length} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>prescribed</span></div>
                  <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: isPolypharmacy ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)', color: isPolypharmacy ? '#fbbf24' : '#34d399', border: `1px solid ${isPolypharmacy ? '#f59e0b55' : '#10b98155'}` }}>
                    {isPolypharmacy ? '⚠️ Polypharmacy (≥ 5)' : '✓ Normal Regimen'}
                  </span>
                </div>

                <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>High-Alert Medications</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: highAlertList.length ? '#f87171' : '#34d399', margin: '4px 0 2px' }}>{highAlertList.length} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>drugs</span></div>
                  <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: highAlertList.length ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)', color: highAlertList.length ? '#f87171' : '#34d399', border: `1px solid ${highAlertList.length ? '#ef444455' : '#10b98155'}` }}>
                    {highAlertList.length ? '⚠️ Double-Sign Required' : '✓ Standard Risk'}
                  </span>
                </div>

                <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Allergy Cross-Check</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: allergyMatches.length ? '#ef4444' : '#34d399', margin: '6px 0 2px' }}>
                    {allergyMatches.length ? '⚠️ Conflict!' : '✓ Cleared'}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{pt.allergies || 'No known allergies'}</div>
                </div>
              </div>

              {/* High-Alert List */}
              {highAlertList.length > 0 && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#f87171', marginBottom: 6 }}>
                    ⚠️ High-Alert Medication Precautions (ISMP Protocol):
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {highAlertList.map(m => (
                      <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', background: 'rgba(0,0,0,0.2)', padding: '6px 10px', borderRadius: 6 }}>
                        <span style={{ fontWeight: 700, color: '#ffd27a' }}>• {m.name}</span>
                        <span style={{ color: '#94a3b8' }}>{m.dose || 'Standard dose'} · {m.frequency || 'PRN'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Safety Instructions */}
              <div style={{ background: 'rgba(10, 25, 48, 0.85)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 800, color: '#38bdf8', marginBottom: 6 }}>
                  🩺 Clinical Practice Guidelines (CPG) Medication Rules:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8rem', color: '#cbd5e1', lineHeight: 1.5 }}>
                  <li><b>Timing &amp; Food:</b> Acid reducers (e.g. Omeprazole) must be taken 30-60 min before meals. Statins best taken at night.</li>
                  <li><b>Hypoglycemia Watch:</b> For diabetic patients, always log HGT before giving insulin or sulfonylureas. Fast-acting glucose must be at bedside.</li>
                  <li><b>Patient Identification:</b> Always verify 2 identifiers (Name &amp; NRIC) before administration.</li>
                </ul>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 14 }}>
                <button
                  type="button"
                  className="ghost"
                  style={{ fontSize: '0.8rem', padding: '8px 14px', background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer' }}
                  onClick={() => {
                    navigator.clipboard.writeText(medReportText);
                    setAiMedCopied(true);
                    setTimeout(() => setAiMedCopied(false), 3000);
                  }}
                >
                  {aiMedCopied ? '✓ Copied Medication Audit!' : '📋 Copy Safety Audit'}
                </button>

                <a
                  href={`https://wa.me/?text=${encodeURIComponent(medReportText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp"
                  style={{ textDecoration: 'none', fontSize: '0.8rem', padding: '8px 14px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#25D366', color: '#fff', fontWeight: 700 }}
                >
                  <span>💬</span> WhatsApp Doctor / Supervisor
                </a>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function blank(kind) {
  return { name: '', dose: '', route: '', frequency: '', times: kind === 'regular' ? 'MORNING' : '',
           start_date: '', end_date: '', indication: '', max_dose: '', notes: '' };
}
function todayMY() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuala_Lumpur',
    year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function addDays(d, n) { const t = new Date(d + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10); }
function mondayOf(d) { const t = new Date(d + 'T00:00:00Z');
  return addDays(d, -((t.getUTCDay() + 6) % 7)); }
function fmt(d) { return d.slice(8, 10) + '/' + d.slice(5, 7); }
function timeOf(ts) { return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kuala_Lumpur',
  hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(ts)); }
